/**
 * Consentement à recevoir les communications commerciales du studio Exidis (RGPD).
 *
 * Porté par le REGISTRE du studio (mode `registre`, propriétaire seulement) : exiPlan n'en garde
 * aucune copie. Le composant lit l'état par `GET /organizations/:id/consentement-marketing` et le
 * pose par `PUT` (`{ statut: 'granted' | 'denied' }`).
 *
 * Jamais pré-coché : la case n'est cochée que si le registre dit `granted` — `unknown` (aucun choix
 * encore fait) et `denied` (refus ou retrait) sont tous deux décochés. L'état affiché est toujours
 * celui que le registre RENVOIE (un `granted` refusé sur une ligne verrouillée revient `denied`) :
 * on ne coche pas « en attendant » la réponse.
 */

import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { reconnectForReauth } from '../lib/reauth.js';
import { useSession } from '../contexts/SessionContext.jsx';
import { useAsync } from '../hooks/useAsync.js';
import { Eyebrow } from './common.jsx';
import { BTN_GHOST_SM, CELLS_PLANCHE, CELL_PLANCHE, CHECK, CHOIX_LABEL } from '../ui/theme.js';

export function MarketingConsentPanel({ organizationId }) {
  const { t } = useTranslation();
  const { user } = useSession();
  const aideId = useId();
  const chemin = `/organizations/${organizationId}/consentement-marketing`;
  const {
    data,
    error: erreurChargement,
    loading,
  } = useAsync((signal) => api.get(chemin, { signal }), [chemin]);
  // Ce que le registre a renvoyé au dernier `PUT` : prime sur la lecture initiale.
  const [retenu, setRetenu] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Donner le consentement exige une connexion de moins de 5 min : on propose de se reconnecter.
  const [reauthRequired, setReauthRequired] = useState(false);

  if (loading) {
    return <p className="text-sm text-ink-soft">{t('organization.marketing.loading')}</p>;
  }
  // Pas de consentement au registre (mode local, rôle sans accès) : la section n'existe pas.
  if (erreurChargement || !data) return null;

  const etat = retenu ?? data.data;
  const granted = etat?.statut === 'granted';
  const refusConserve = etat?.statut === 'denied' && etat?.verrouille === true;

  const changer = async (event) => {
    const voulu = event.target.checked;
    setBusy(true);
    setError(null);
    setReauthRequired(false);
    try {
      const reponse = await api.put(chemin, { statut: voulu ? 'granted' : 'denied' });
      setRetenu(reponse.data);
    } catch (err) {
      if (err.status === 401 && err.details?.code === 'REAUTH_REQUIRED') setReauthRequired(true);
      else setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${CELLS_PLANCHE} grid-cols-1`}>
      <section className={`${CELL_PLANCHE} flex flex-col items-start gap-3 bg-paper p-6`}>
        <span className="ticks" aria-hidden="true" />
        <Eyebrow>{t('organization.marketing.title')}</Eyebrow>
        <label className={CHOIX_LABEL}>
          <input
            className={CHECK}
            type="checkbox"
            checked={granted}
            disabled={busy}
            aria-describedby={aideId}
            onChange={changer}
          />
          {t('organization.marketing.label')}
        </label>
        <p id={aideId} className="text-xs text-ink-faint">
          {t('organization.marketing.help')}{' '}
          <Link to="/confidentialite" className="underline">
            {t('organization.marketing.privacyLink')}
          </Link>
        </p>
        {refusConserve && (
          <p role="note" className="text-sm text-ink">
            {t('organization.marketing.refusConserve')}
          </p>
        )}
        {busy && (
          <p role="status" className="text-xs text-ink-faint">
            {t('organization.marketing.saving')}
          </p>
        )}
        {reauthRequired && (
          <div className="flex flex-col items-start gap-2 border-2 border-accent-trait bg-accent-wash px-3 py-2">
            <p role="alert" className="text-sm text-accent-ink">
              {t('organization.marketing.reauthRequired')}
            </p>
            <button
              type="button"
              className={BTN_GHOST_SM}
              onClick={() => reconnectForReauth(`/organisation/${organizationId}`, user)}
            >
              {t('organization.danger.reconnect')}
            </button>
          </div>
        )}
        {error && (
          <p role="alert" className="text-sm text-statut-erreur">
            {t('organization.marketing.saveError', { message: error.message })}
          </p>
        )}
      </section>
    </div>
  );
}
